import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Plus,
  Stethoscope,
  ChevronRight,
  Dog,
  FileText,
  Building2,
  Calendar,
  Syringe,
  Bell,
  Paperclip,
  Pencil,
  Scale,
  Activity,
  LayoutGrid,
  QrCode,
  Copy,
  Link2,
  Share2,
  X,
} from 'lucide-react'
import { mascotaService } from '../../services/mascotaService'
import { registroPesoService } from '../../services/registroPesoService'
import { mascotaHistorialService } from '../../services/mascotaHistorialService'
import { citaService } from '../../services/citaService'
import { fichaService } from '../../services/fichaService'
import { accesoMascotasService } from '../../services/accesoMascotasService'
import { usePermissions } from '../../hooks/usePermissions'
import LoadingState from '../../components/ui/LoadingState'
import EmptyState from '../../components/ui/EmptyState'
import { normalizeFotoUrl } from '../../utils/avatarHelpers'

function extractArray(payload) {
  if (!payload) return []
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.data?.data)) return payload.data.data
  if (Array.isArray(payload?.condiciones)) return payload.condiciones
  if (Array.isArray(payload?.registros)) return payload.registros
  if (Array.isArray(payload?.registros_peso)) return payload.registros_peso
  return []
}

function extractEntity(payload) {
  if (!payload) return null
  if (payload?.data?.data && typeof payload.data.data === 'object') return payload.data.data
  if (payload?.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) return payload.data
  if (typeof payload === 'object' && !Array.isArray(payload)) return payload
  return null
}

function labelTipoRaza(t) {
  if (!t) return ''
  const m = { pura: 'Pura', cruza: 'Cruza', mixto: 'Mixto' }
  return m[t] || t
}

function getMascotaId(item) {
  return (
    item?.mascota?.id_mascota ||
    item?.mascota?.idMascota ||
    item?.mascota?.id ||
    item?.id_mascota ||
    item?.idMascota ||
    item?.mascota_id ||
    item?.mascotaId ||
    item?.id_mascota_fk ||
    item?.idMascotaFk
  )
}

/** Fichas embebidas en la carga principal de mascota (fichas_clinicas). */
function extractFichasFromMascota(mascota) {
  if (!mascota) return []
  const raw =
    mascota.fichas_clinicas ||
    mascota.fichasClinicas ||
    mascota.fichas ||
    []
  return Array.isArray(raw) ? raw : []
}

function dedupeFichas(arr) {
  const seen = new Set()
  const out = []
  for (const f of arr) {
    const fid = f?.id_ficha ?? f?.idFicha
    const key = fid != null && fid !== '' ? `id:${fid}` : `idx:${JSON.stringify(f?.fecha || '')}:${f?.motivo_consulta || ''}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(f)
  }
  return out
}

function sortFichasByDateDesc(arr) {
  return [...arr].sort((a, b) => {
    const ta = new Date(a?.fecha || a?.fecha_consulta || a?.fechaConsulta || 0).getTime()
    const tb = new Date(b?.fecha || b?.fecha_consulta || b?.fechaConsulta || 0).getTime()
    return tb - ta
  })
}

function normalizeDate(value) {
  if (!value) return 'Sin fecha'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Sin fecha'
  return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'long' })
}

function normalizeDateTime(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })
}

function absoluteStorageUrl(url) {
  if (!url) return null
  if (String(url).startsWith('http')) return url
  const base = (import.meta.env.VITE_API_URL || '').replace(/\/api\/?$/, '') || window.location.origin
  return String(url).startsWith('/') ? `${base}${url}` : `${base}/${url}`
}

export default function MascotaDetailMobileExact() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const initialMascota = location.state?.mascota || null
  const [activeTab, setActiveTab] = useState('Resumen')
  const condicionFocusRef = useRef(null)
  const fichaFocusRef = useRef(null)
  const [mascota, setMascota] = useState(initialMascota)
  const [appointmentsApi, setAppointmentsApi] = useState([])
  const [condicionesApi, setCondicionesApi] = useState([])
  const [fichasApi, setFichasApi] = useState([])
  const [pesoRegistrosApi, setPesoRegistrosApi] = useState([])
  const [alturaRegistrosApi, setAlturaRegistrosApi] = useState([])
  const [vacunasApi, setVacunasApi] = useState([])
  const [desparasApi, setDesparasApi] = useState([])
  const [recordatoriosApi, setRecordatoriosApi] = useState([])
  const [adjuntosApi, setAdjuntosApi] = useState([])
  const [loading, setLoading] = useState(!initialMascota)
  const { isDueno, isPetSitter } = usePermissions()
  const [shareOpen, setShareOpen] = useState(false)
  const [shareForm, setShareForm] = useState({ alcance: 'HISTORIAL_COMPLETO', duracion_minutos: 60 })
  const [shareResult, setShareResult] = useState(null)
  const [sharing, setSharing] = useState(false)
  const [shareError, setShareError] = useState(null)
  const [weightOpen, setWeightOpen] = useState(false)
  const [weightSaving, setWeightSaving] = useState(false)
  const [weightError, setWeightError] = useState(null)
  const [weightSuccess, setWeightSuccess] = useState(null)
  const [weightForm, setWeightForm] = useState({
    peso: '',
    fecha_registro: new Date().toISOString().split('T')[0],
    tipo_registro: 'CASA',
    observaciones: '',
  })

  useEffect(() => {
    const params = new URLSearchParams(location.search || '')
    const tab = params.get('tab')
    if (tab && ['Resumen', 'Historial', 'Salud', 'Fichas'].includes(tab)) {
      setActiveTab(tab)
    }
  }, [location.search])

  useEffect(() => {
    const params = new URLSearchParams(location.search || '')
    const condicionId = params.get('condicion')
    condicionFocusRef.current = condicionId ? String(condicionId) : null
  }, [location.search])

  useEffect(() => {
    const params = new URLSearchParams(location.search || '')
    const fichaId = params.get('ficha')
    fichaFocusRef.current = fichaId ? String(fichaId) : null
  }, [location.search])

  useEffect(() => {
    const fetchMascota = async () => {
      if (!id) return
      try {
        const initialId = initialMascota?.id_mascota || initialMascota?.idMascota
        const canReuseInitial = !!initialMascota && String(initialId) === String(id)

        setLoading(!canReuseInitial)
        const [
          response,
          condicionesResponse,
          pesoResponse,
          citasResponse,
          fichasResponse,
          alturaRes,
          vacunasRes,
          desparasRes,
          recordatoriosRes,
          adjuntosRes
        ] = await Promise.all([
          mascotaService.getById(id),
          mascotaService.getCondiciones(id).catch(() => null),
          mascotaService.getRegistrosPeso(id).catch(() => null),
          citaService.getCitasByDueno().catch(() => citaService.getAll()).catch(() => null),
          fichaService
            .getAll({
              id_mascota: id,
              idMascota: id,
              mascota_id: id,
              mascotaId: id,
              per_page: 200
            })
            .catch(() => null),
          mascotaHistorialService.getRegistrosAltura(id, { per_page: 100 }).catch(() => ({ data: [] })),
          mascotaHistorialService.getRegistrosVacunas(id, { per_page: 100 }).catch(() => ({ data: [] })),
          mascotaHistorialService.getRegistrosDesparasitacion(id, { per_page: 100 }).catch(() => ({ data: [] })),
          mascotaHistorialService.getRecordatorios(id, { per_page: 100 }).catch(() => ({ data: [] })),
          mascotaHistorialService.getAdjuntos(id, { per_page: 100 }).catch(() => ({ data: [] }))
        ])

        const data = extractEntity(response) ?? (canReuseInitial ? initialMascota : null)
        const pesoData = extractArray(pesoResponse)
        const pesoSorted = [...pesoData].sort((a, b) => {
          const ta = new Date(a?.fecha || a?.fecha_registro || a?.fechaRegistro || 0).getTime()
          const tb = new Date(b?.fecha || b?.fecha_registro || b?.fechaRegistro || 0).getTime()
          return tb - ta
        })
        setPesoRegistrosApi(pesoSorted)
        setAlturaRegistrosApi(alturaRes?.data || [])
        setVacunasApi(vacunasRes?.data || [])
        setDesparasApi(desparasRes?.data || [])
        setRecordatoriosApi(recordatoriosRes?.data || [])
        setAdjuntosApi(adjuntosRes?.data || [])
        const condicionesData = extractArray(condicionesResponse)
        const citasData = extractArray(citasResponse)
        const fichasData = extractArray(fichasResponse)
        const citasMascota = citasData.filter((cita) => {
          const mascotaId = getMascotaId(cita)
          return String(mascotaId) === String(id)
        })

        /** Fichas que vienen en el perfil de la mascota en respuestas consolidadas. */
        const fromMascota = extractFichasFromMascota(data)

        let fichasMascota = fichasData.filter((ficha) => {
          const mascotaId = getMascotaId(ficha)
          if (mascotaId != null && mascotaId !== '') {
            return String(mascotaId) === String(id)
          }
          return false
        })

        // Fallback: listado general filtrado por id mascota (incluye variantes de anidación)
        if (fichasMascota.length === 0 && fromMascota.length === 0) {
          const fichasFallback = await fichaService.getAll({ per_page: 300 }).catch(() => null)
          const fichasFallbackData = extractArray(fichasFallback)
          fichasMascota = fichasFallbackData.filter((ficha) => String(getMascotaId(ficha)) === String(id))
        }

        const mergedFichas = sortFichasByDateDesc(dedupeFichas([...fromMascota, ...fichasMascota]))

        setMascota(data || null)
        setCondicionesApi(condicionesData)
        setAppointmentsApi(citasMascota)
        setFichasApi(mergedFichas)
      } catch (error) {
        console.error('Error loading pet detail:', error)
        if (!initialMascota) setMascota(null)
        setCondicionesApi([])
        setAppointmentsApi([])
        setFichasApi([])
        setPesoRegistrosApi([])
        setAlturaRegistrosApi([])
        setVacunasApi([])
        setDesparasApi([])
        setRecordatoriosApi([])
        setAdjuntosApi([])
      } finally {
        setLoading(false)
      }
    }

    fetchMascota()
  }, [id, initialMascota])

  const petImage = useMemo(() => {
    const value = mascota?.foto || mascota?.avatar_url
    return value ? normalizeFotoUrl(value) : '/placeholder-pet.svg'
  }, [mascota])

  const appointments = useMemo(() => {
    if (appointmentsApi.length > 0) return appointmentsApi
    return Array.isArray(mascota?.citas) ? mascota.citas : []
  }, [appointmentsApi, mascota])

  const citasPendientes = useMemo(() => {
    const pendingStates = ['PENDIENTE', 'APROBADA', 'CONFIRMADA']
    return appointments.filter((item) => pendingStates.includes(String(item.estado || '').toUpperCase()))
  }, [appointments])

  const citasProximasOrdenadas = useMemo(() => {
    return [...citasPendientes].sort((a, b) => {
      const ta = new Date(a?.fecha || a?.fecha_cita || a?.fechaCita || 0).getTime()
      const tb = new Date(b?.fecha || b?.fecha_cita || b?.fechaCita || 0).getTime()
      return ta - tb
    })
  }, [citasPendientes])

  const vacunasSorted = useMemo(() => {
    return [...vacunasApi].sort((a, b) => {
      const ta = new Date(a?.fecha_aplicacion || a?.fechaAplicacion || 0).getTime()
      const tb = new Date(b?.fecha_aplicacion || b?.fechaAplicacion || 0).getTime()
      return tb - ta
    })
  }, [vacunasApi])

  const desparasSorted = useMemo(() => {
    return [...desparasApi].sort((a, b) => {
      const ta = new Date(a?.fecha_aplicacion || a?.fechaAplicacion || 0).getTime()
      const tb = new Date(b?.fecha_aplicacion || b?.fechaAplicacion || 0).getTime()
      return tb - ta
    })
  }, [desparasApi])

  const proximosRecordatorios = useMemo(() => {
    const now = Date.now()
    return [...recordatoriosApi]
      .filter((r) => {
        const t = new Date(r?.fecha_hora || r?.fechaHora || 0).getTime()
        return !Number.isNaN(t) && t >= now - 86400000
      })
      .sort((a, b) => {
        const ta = new Date(a?.fecha_hora || a?.fechaHora || 0).getTime()
        const tb = new Date(b?.fecha_hora || b?.fechaHora || 0).getTime()
        return ta - tb
      })
      .slice(0, 6)
  }, [recordatoriosApi])

  const condicionesCount = condicionesApi.length

  const ultimaVacuna = vacunasSorted[0]
  const ultimaDesparas = desparasSorted[0]

  const historialAppointments = useMemo(() => {
    const list = appointments.filter((item) => String(item.estado || '').toUpperCase().includes('COMPLET'))
    return list.length > 0 ? list : appointments
  }, [appointments])

  const preventives = useMemo(() => {
    const source = condicionesApi.length > 0
      ? condicionesApi
      : (Array.isArray(mascota?.condiciones_activas) ? mascota.condiciones_activas : [])

    if (source.length > 0) {
      return source.slice(0, 3).map((item) => ({
        id: item.id || item.id_condicion,
        title: item.condicion_salud?.nombre || item.condicionSalud?.nombre || 'Seguimiento preventivo',
        subtitle: item.estado || 'En control'
      }))
    }
    return []
  }, [condicionesApi, mascota])

  const condicionesForDashboard = useMemo(() => {
    const source = condicionesApi.length > 0
      ? condicionesApi
      : (Array.isArray(mascota?.condiciones_activas) ? mascota.condiciones_activas : [])

    return (source || []).map((item) => {
      const idCond = item.id || item.id_condicion || item.id_mascota_condicion || item.idMascotaCondicion
      const nombre = item.condicion_salud?.nombre || item.condicionSalud?.nombre || item.nombre || 'Condición'
      const estado = item.estado || item.status || '—'
      const severidad = item.severidad || item.severity || null
      const fecha = item.fecha_diagnostico || item.fechaDiagnostico || item.fecha || null
      return { raw: item, id: idCond != null ? String(idCond) : null, nombre, estado, severidad, fecha }
    })
  }, [condicionesApi, mascota])

  useEffect(() => {
    if (activeTab !== 'Salud') return
    const targetId = condicionFocusRef.current
    if (!targetId) return
    // Intentar scroll suave a la condición (si existe en el DOM).
    const el = document.getElementById(`condicion-${targetId}`)
    if (el && typeof el.scrollIntoView === 'function') {
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 150)
    }
  }, [activeTab, condicionesForDashboard])

  const fichasCount = fichasApi.length

  const pesoMasReciente = pesoRegistrosApi[0]

  const tabItems = useMemo(
    () => [
      { id: 'Resumen', label: 'Panel' },
      { id: 'Historial', label: 'Historial' },
      { id: 'Salud', label: 'Salud' },
      { id: 'Fichas', label: fichasCount > 0 ? `Fichas (${fichasCount})` : 'Fichas' },
    ],
    [fichasCount]
  )

  const edadMascotaLabel = useMemo(() => {
    const fn = mascota?.fecha_nacimiento || mascota?.fechaNacimiento
    if (!fn) return null
    const birth = new Date(fn)
    if (Number.isNaN(birth.getTime())) return null
    const today = new Date()
    let years = today.getFullYear() - birth.getFullYear()
    let months = today.getMonth() - birth.getMonth()
    if (months < 0) {
      years--
      months += 12
    }
    if (years > 0) return `${years} año${years === 1 ? '' : 's'}`
    return `${months} mes${months === 1 ? '' : 'es'}`
  }, [mascota])

  const mascotaIdRoute = id || mascota?.id_mascota || mascota?.idMascota || ''
  const canRegisterWeight = isDueno || isPetSitter

  const shareUrl = useMemo(() => {
    if (!shareResult) return ''
    if (shareResult.share_url) return shareResult.share_url
    return `${window.location.origin}/acceso-mascotas?token=${encodeURIComponent(shareResult.token)}`
  }, [shareResult])

  const shareQrUrl = useMemo(() => {
    if (!shareUrl) return ''
    return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(shareUrl)}`
  }, [shareUrl])

  const copyText = async (value) => {
    if (!value) return
    try {
      await navigator.clipboard.writeText(value)
    } catch (_) {
      // noop
    }
  }

  const createTemporaryShare = async () => {
    try {
      setSharing(true)
      setShareError(null)
      setShareResult(null)
      const resp = await accesoMascotasService.create({
        mascotas: [Number(mascotaIdRoute)],
        alcance: shareForm.alcance,
        duracion_minutos: Number(shareForm.duracion_minutos) || 60,
      })
      if (resp?.success) setShareResult(resp.data)
      else setShareError(resp?.error || 'No se pudo generar el acceso temporal')
    } catch (err) {
      setShareError(err.response?.data?.error || err.message || 'No se pudo generar el acceso temporal')
    } finally {
      setSharing(false)
    }
  }

  const nativeShareLink = async () => {
    if (!shareUrl) return
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Acceso temporal de ${mascota?.nombre || 'mascota'}`,
          text: 'Acceso temporal por enlace/QR',
          url: shareUrl,
        })
      } else {
        await copyText(shareUrl)
      }
    } catch (_) {
      // noop
    }
  }

  const savePesoRegistro = async () => {
    try {
      setWeightSaving(true)
      setWeightError(null)
      setWeightSuccess(null)

      const value = Number(weightForm.peso)
      if (!Number.isFinite(value) || value <= 0) {
        setWeightError('Ingresa un peso válido mayor a 0.')
        setWeightSaving(false)
        return
      }

      const resp = await registroPesoService.create({
        id_mascota: Number(mascotaIdRoute),
        peso: value,
        fecha_registro: weightForm.fecha_registro,
        tipo_registro: weightForm.tipo_registro || 'CASA',
        observaciones: weightForm.observaciones || undefined,
      })

      if (resp?.success === false) {
        setWeightError(resp?.error || 'No se pudo registrar el pesaje.')
        return
      }

      const updatedPeso = await mascotaService.getRegistrosPeso(mascotaIdRoute).catch(() => null)
      const pesoData = extractArray(updatedPeso)
      const pesoSorted = [...pesoData].sort((a, b) => {
        const ta = new Date(a?.fecha || a?.fecha_registro || a?.fechaRegistro || 0).getTime()
        const tb = new Date(b?.fecha || b?.fecha_registro || b?.fechaRegistro || 0).getTime()
        return tb - ta
      })
      setPesoRegistrosApi(pesoSorted)
      setWeightSuccess('Pesaje registrado correctamente.')
      setWeightForm((prev) => ({
        ...prev,
        peso: '',
        observaciones: '',
        fecha_registro: new Date().toISOString().split('T')[0],
      }))
    } catch (err) {
      setWeightError(err.response?.data?.error || err.message || 'No se pudo registrar el pesaje.')
    } finally {
      setWeightSaving(false)
    }
  }

  if (loading) return <LoadingState message="Cargando perfil..." />

  if (!mascota) {
    return (
      <div className="px-4 py-6">
        <EmptyState icon={Dog} title="Mascota no encontrada" message="No pudimos cargar la información seleccionada." />
      </div>
    )
  }

  return (
    <div className="min-h-full bg-white pb-20">
      <section className="overflow-hidden">
        <div className="relative h-[188px] bg-gradient-to-b from-[#8aab6c] to-[#c9a96e]">
          <img src={petImage} alt={mascota.nombre || 'Mascota'} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-black/10 to-transparent" />
          <button
            onClick={() => navigate(-1)}
            className="absolute left-3 top-3 h-8 w-8 rounded-full bg-transparent text-white flex items-center justify-center"
            type="button"
            aria-label="Volver"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => navigate(`/mascotas/${mascotaIdRoute}/edit`)}
            className="absolute right-3 top-3 h-8 px-3 rounded-full bg-white/90 text-[#0f1623] border border-white/70 flex items-center justify-center gap-1.5"
            type="button"
            aria-label="Editar mascota"
            title="Editar"
          >
            <Pencil className="w-4 h-4" />
            <span className="text-[12px] font-semibold">Editar</span>
          </button>
          <div className="absolute left-4 bottom-3 text-white pr-3">
            <h1 className="text-[24px] leading-none font-bold tracking-tight">{mascota.nombre || 'Mascota'}</h1>
            <p className="text-[11px] opacity-90 mt-1">
              {mascota.raza || mascota.especie || 'Sin especificar'}
              {(mascota.tipo_raza || mascota.tipoRaza) && (
                <span> · {labelTipoRaza(mascota.tipo_raza || mascota.tipoRaza)}</span>
              )}
              {edadMascotaLabel ? <span> · {edadMascotaLabel}</span> : null}
            </p>
          </div>
        </div>
        <div className="mx-4 mt-3 flex gap-2">
          <button
            onClick={() => navigate(`/citas?mascota=${mascotaIdRoute}&nuevo=1`)}
            className="flex-1 h-11 rounded-[24px] bg-[#5b87d8] text-white text-sm font-semibold shadow-sm"
            type="button"
          >
            Solicitar cita
          </button>
          <button
            onClick={() => navigate(`/mascotas/${mascotaIdRoute}/edit`)}
            className="flex-1 h-11 rounded-[24px] border-2 border-[#5b87d8] bg-white text-[#5b87d8] text-sm font-semibold inline-flex items-center justify-center gap-1.5"
            type="button"
          >
            <Pencil className="w-4 h-4" />
            Editar
          </button>
        </div>
        {isDueno ? (
          <div className="mx-4 mt-2">
            <button
              type="button"
              onClick={() => {
                setShareOpen(true)
                setShareForm({ alcance: 'HISTORIAL_COMPLETO', duracion_minutos: 60 })
                setShareResult(null)
                setShareError(null)
              }}
              className="w-full h-10 rounded-[20px] border border-[#0f1623]/20 bg-white text-[#0f1623] text-sm font-semibold inline-flex items-center justify-center gap-1.5"
            >
              <QrCode className="w-4 h-4" />
              Compartir temporalmente
            </button>
          </div>
        ) : null}
        <div className="mt-3 px-4 border-b border-[#f0f0f0] overflow-x-auto">
          <div className="flex gap-1 min-w-max">
            {tabItems.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-2.5 py-2 text-[12px] ${
                  activeTab === tab.id ? 'text-[#5b87d8] border-b-2 border-[#5b87d8] font-semibold' : 'text-[#aaa]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {(mascota.origen || mascota.criadero || mascota.fecha_adopcion || mascota.fechaAdopcion) && (
          <div className="mx-4 mt-2 rounded-[12px] bg-[#f4faf4] border border-[#d8ead8] px-3 py-2.5 text-[12px] text-[#333]">
            <p className="text-[10px] font-bold uppercase tracking-wide text-[#4a7a4a] mb-1">Procedencia</p>
            <p>
              <span className="font-semibold">{mascota.origen || '—'}</span>
              {mascota.criadero ? (
                <span className="text-[#555]"> · {mascota.criadero}</span>
              ) : null}
            </p>
            {(mascota.fecha_adopcion || mascota.fechaAdopcion) && (
              <p className="text-[11px] text-[#666] mt-1">
                {mascota.origen === 'Comprado' ? 'Compra' : 'Adopción'}:{' '}
                {new Date(mascota.fecha_adopcion || mascota.fechaAdopcion).toLocaleDateString('es-ES')}
              </p>
            )}
          </div>
        )}
      </section>

      {activeTab === 'Resumen' && (
      <section className="px-4 py-3 space-y-4 pb-8">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 min-w-0">
            <div className="h-10 w-10 rounded-2xl bg-[#eef3fb] flex items-center justify-center shrink-0">
              <LayoutGrid className="w-5 h-5 text-[#5b87d8]" />
            </div>
            <div>
              <h2 className="text-[16px] font-bold text-[#111] leading-tight">Panel de la mascota</h2>
              <p className="text-[11px] text-slate-500 mt-0.5">Citas, salud, fichas y más en un vistazo</p>
            </div>
          </div>
        </div>

        {(mascota.sexo || mascota.color || mascota.microchip) && (
          <div className="flex flex-wrap gap-1.5">
            {mascota.sexo ? (
              <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">
                {String(mascota.sexo).toUpperCase().startsWith('M') && !String(mascota.sexo).toUpperCase().includes('HEMB')
                  ? 'Macho'
                  : String(mascota.sexo).toUpperCase().startsWith('H') || String(mascota.sexo).toUpperCase().includes('HEMBRA')
                    ? 'Hembra'
                    : mascota.sexo}
              </span>
            ) : null}
            {mascota.color ? (
              <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-900 border border-amber-100">
                {mascota.color}
              </span>
            ) : null}
            {mascota.microchip ? (
              <span className="text-[10px] font-mono px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-900 border border-emerald-100 truncate max-w-full">
                Chip: {mascota.microchip}
              </span>
            ) : null}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setActiveTab('Fichas')}
            className="rounded-[16px] border border-[#e4e9f0] bg-[#f8fafc] p-3 text-left active:scale-[0.99] transition-transform shadow-sm"
          >
            <FileText className="w-4 h-4 text-[#0f1623] mb-1" />
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Fichas</p>
            <p className="text-[22px] font-bold text-[#111] leading-tight">{fichasCount}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Clínicas</p>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('Historial')}
            className="rounded-[16px] border border-[#dbeafe] bg-[#eff6ff] p-3 text-left active:scale-[0.99] transition-transform shadow-sm"
          >
            <Calendar className="w-4 h-4 text-[#5b87d8] mb-1" />
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Citas activas</p>
            <p className="text-[22px] font-bold text-[#111] leading-tight">{citasPendientes.length}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Pendientes / confirmadas</p>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('Salud')}
            className="rounded-[16px] border border-[#e8f5e9] bg-[#f4faf4] p-3 text-left active:scale-[0.99] transition-transform shadow-sm"
          >
            <Scale className="w-4 h-4 text-[#4a8a28] mb-1" />
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Peso</p>
            <p className="text-[18px] font-bold text-[#111] leading-tight truncate">
              {pesoMasReciente?.peso ?? pesoMasReciente?.valor ?? mascota?.peso ?? '—'}{' '}
              {pesoMasReciente || mascota?.peso != null ? <span className="text-[12px] font-semibold">kg</span> : null}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5 truncate">
              {pesoMasReciente ? normalizeDate(pesoMasReciente.fecha || pesoMasReciente.fecha_registro || pesoMasReciente.fechaRegistro) : 'Sin registro'}
            </p>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('Historial')}
            className="rounded-[16px] border border-[#fce7f3] bg-[#fdf2f8] p-3 text-left active:scale-[0.99] transition-transform shadow-sm"
          >
            <Syringe className="w-4 h-4 text-pink-600 mb-1" />
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Vacunas</p>
            <p className="text-[22px] font-bold text-[#111] leading-tight">{vacunasApi.length}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Registros</p>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('Historial')}
            className="rounded-[16px] border border-[#f3e8ff] bg-[#faf5ff] p-3 text-left active:scale-[0.99] transition-transform shadow-sm"
          >
            <Activity className="w-4 h-4 text-violet-600 mb-1" />
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Desparas.</p>
            <p className="text-[22px] font-bold text-[#111] leading-tight">{desparasApi.length}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Aplicaciones</p>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('Historial')}
            className="rounded-[16px] border border-[#fef3c7] bg-[#fffbeb] p-3 text-left active:scale-[0.99] transition-transform shadow-sm"
          >
            <Bell className="w-4 h-4 text-amber-600 mb-1" />
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Avisos</p>
            <p className="text-[22px] font-bold text-[#111] leading-tight">{recordatoriosApi.length}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Recordatorios</p>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('Salud')}
            className="rounded-[16px] border border-[#e0f2fe] bg-[#f0f9ff] p-3 text-left active:scale-[0.99] transition-transform shadow-sm"
          >
            <Stethoscope className="w-4 h-4 text-sky-600 mb-1" />
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Condiciones</p>
            <p className="text-[22px] font-bold text-[#111] leading-tight">{condicionesCount}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">En seguimiento</p>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('Historial')}
            className="rounded-[16px] border border-[#f1f5f9] bg-white p-3 text-left active:scale-[0.99] transition-transform shadow-sm"
          >
            <Paperclip className="w-4 h-4 text-slate-600 mb-1" />
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Adjuntos</p>
            <p className="text-[22px] font-bold text-[#111] leading-tight">{adjuntosApi.length}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Archivos / notas</p>
          </button>
        </div>

        {proximosRecordatorios.length > 0 && (
          <div className="rounded-[16px] border border-amber-100 bg-[#fffbeb] px-3 py-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[13px] font-bold text-[#111] flex items-center gap-1.5">
                <Bell className="w-4 h-4 text-amber-600" />
                Próximos recordatorios
              </h3>
              <button type="button" className="text-[11px] text-[#5b87d8] font-semibold" onClick={() => setActiveTab('Historial')}>
                Ver todo
              </button>
            </div>
            <ul className="space-y-2">
              {proximosRecordatorios.map((row, idx) => (
                <li key={row.id_recordatorio || idx} className="text-[12px] text-slate-700 border-b border-amber-100/80 pb-2 last:border-0 last:pb-0">
                  <p className="font-semibold text-[#111]">{row.titulo}</p>
                  <p className="text-[11px] text-slate-500">{normalizeDateTime(row.fecha_hora || row.fechaHora)}</p>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[14px] font-bold text-[#111]">Próximas citas</h3>
            <button type="button" className="text-[12px] text-[#5b87d8] font-semibold" onClick={() => navigate('/citas')}>
              Calendario
            </button>
          </div>
          {citasProximasOrdenadas.length > 0 ? (
            <div className="space-y-2">
              {citasProximasOrdenadas.slice(0, 4).map((appt, idx) => {
                const idCita = appt.id_cita || appt.idCita
                return (
                  <button
                    key={idCita || idx}
                    onClick={() => idCita && navigate(`/citas/${idCita}`)}
                    type="button"
                    className={`w-full rounded-[14px] px-3 py-3 text-left ${idx % 2 === 0 ? 'bg-[#5b87d8] text-white' : 'bg-[#c9883a] text-white'} relative overflow-hidden`}
                  >
                    <div className="absolute -right-4 -bottom-4 h-12 w-12 rounded-full bg-white/20" />
                    <p className="text-[13px] font-semibold">{appt.tipo_cita || appt.tipoCita || 'Consulta'}</p>
                    <p className="text-[11px] opacity-90">{normalizeDate(appt.fecha || appt.fecha_cita || appt.fechaCita)}</p>
                    <span className="mt-1 inline-block text-[9px] font-bold bg-white/25 rounded-[8px] px-2 py-0.5">{appt.estado || '—'}</span>
                  </button>
                )
              })}
            </div>
          ) : (
            <button
              onClick={() => navigate(`/citas?mascota=${mascotaIdRoute}&nuevo=1`)}
              type="button"
              className="w-full rounded-2xl border border-dashed border-[#cbd5e1] bg-[#f8fafc] py-4 text-sm text-slate-600 font-medium"
            >
              No hay citas activas · Pedir cita
            </button>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[14px] font-bold text-[#111]">Últimas fichas clínicas</h3>
            <button type="button" className="text-[12px] text-[#5b87d8] font-semibold" onClick={() => setActiveTab('Fichas')}>
              Ver todas{fichasCount > 0 ? ` (${fichasCount})` : ''}
            </button>
          </div>
          {fichasApi.length > 0 ? (
            <div className="space-y-2">
              {fichasApi.slice(0, 2).map((ficha, idx) => {
                const fichaId = ficha.id_ficha || ficha.idFicha
                const titulo =
                  ficha.diagnostico ||
                  ficha.motivo_consulta ||
                  ficha.motivoConsulta ||
                  (ficha.numero_ficha || ficha.numeroFicha ? `Ficha ${ficha.numero_ficha || ficha.numeroFicha}` : 'Ficha clínica')
                return (
                  <button
                    key={fichaId || `pf-${idx}`}
                    type="button"
                    onClick={() => (fichaId ? navigate(`/fichas/${fichaId}`) : setActiveTab('Fichas'))}
                    className="w-full rounded-[14px] border border-[#e6ebf4] bg-white px-3 py-3 text-left shadow-sm"
                  >
                    <p className="text-[13px] font-semibold text-[#111] line-clamp-2">{titulo}</p>
                    <p className="text-[11px] text-slate-500 mt-1">{normalizeDate(ficha.fecha || ficha.fecha_consulta || ficha.fechaConsulta)}</p>
                  </button>
                )
              })}
            </div>
          ) : (
            <p className="text-[12px] text-slate-500 rounded-[14px] border border-dashed border-[#e2e8f0] bg-[#f8fafc] px-3 py-3">
              Aún no hay fichas vinculadas. Cuando tu veterinario registre visitas, aparecerán aquí.
            </p>
          )}
        </div>

        <div className="rounded-[16px] border border-[#e8f0e8] bg-gradient-to-br from-[#f4faf4] to-white px-3 py-3">
          <h3 className="text-[13px] font-bold text-[#111] mb-2 flex items-center gap-1.5">
            <Activity className="w-4 h-4 text-[#4a8a28]" />
            Salud en un vistazo
          </h3>
          <div className="grid gap-2 text-[12px] text-slate-600">
            <div className="flex justify-between gap-2">
              <span>Última vacuna</span>
              <span className="font-semibold text-[#111] text-right">
                {ultimaVacuna?.nombre_vacuna || '—'}
                {ultimaVacuna ? (
                  <span className="block text-[10px] font-normal text-slate-500">
                    {normalizeDate(ultimaVacuna.fecha_aplicacion || ultimaVacuna.fechaAplicacion)}
                  </span>
                ) : null}
              </span>
            </div>
            <div className="flex justify-between gap-2 border-t border-[#e8f0e8] pt-2">
              <span>Última desparasitación</span>
              <span className="font-semibold text-[#111] text-right">
                {ultimaDesparas?.producto || '—'}
                {ultimaDesparas ? (
                  <span className="block text-[10px] font-normal text-slate-500">
                    {normalizeDate(ultimaDesparas.fecha_aplicacion || ultimaDesparas.fechaAplicacion)}
                  </span>
                ) : null}
              </span>
            </div>
            <div className="flex justify-between gap-2 border-t border-[#e8f0e8] pt-2">
              <span>Rango de peso ideal</span>
              <span className="font-semibold text-[#111] text-right">
                {mascota?.peso_ideal?.peso_minimo ?? '—'} – {mascota?.peso_ideal?.peso_maximo ?? '—'} kg
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setActiveTab('Salud')}
            className="mt-3 w-full text-[12px] font-semibold text-[#4a8a28] py-2 rounded-xl bg-white/80 border border-[#d8ead8]"
          >
            Ver detalle de salud y peso
          </button>
        </div>

        {preventives.length > 0 && (
          <div>
            <h3 className="text-[14px] font-bold text-[#111] mb-2">Condiciones en seguimiento</h3>
            <div className="space-y-2">
              {preventives.map((item) => (
                <div
                  key={item.id}
                  className="rounded-2xl border border-pastel-pink bg-pastel-cream/60 px-3 py-2.5 flex items-center justify-between gap-2"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-pastel-ink truncate">{item.title}</p>
                    <p className="text-xs text-slate-500 truncate">{item.subtitle}</p>
                  </div>
                  <Stethoscope className="w-4 h-4 text-pastel-lavender shrink-0" />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-[16px] bg-[#f7f9fc] border border-[#e8edf4] p-3">
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-2">Accesos rápidos</p>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setActiveTab('Historial')}
              className="h-10 rounded-xl border border-white bg-white px-2 text-[12px] font-semibold text-[#333] shadow-sm flex items-center justify-center gap-1"
            >
              <Calendar className="w-3.5 h-3.5 text-[#5b87d8]" />
              Historial
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('Salud')}
              className="h-10 rounded-xl border border-white bg-white px-2 text-[12px] font-semibold text-[#333] shadow-sm flex items-center justify-center gap-1"
            >
              <Scale className="w-3.5 h-3.5 text-[#4a8a28]" />
              Salud
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('Fichas')}
              className="h-10 rounded-xl border border-white bg-white px-2 text-[12px] font-semibold text-[#333] shadow-sm flex items-center justify-center gap-1"
            >
              <FileText className="w-3.5 h-3.5 text-[#0f1623]" />
              Fichas
            </button>
            <button
              type="button"
              onClick={() => navigate(`/citas?mascota=${mascotaIdRoute}&nuevo=1`)}
              className="h-10 rounded-xl border border-white bg-white px-2 text-[12px] font-semibold text-[#333] shadow-sm flex items-center justify-center gap-1"
            >
              <Plus className="w-3.5 h-3.5 text-[#5b87d8]" />
              Nueva cita
            </button>
          </div>
        </div>
      </section>
      )}

      {activeTab === 'Historial' && (
      <section className="px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-[15px] font-bold text-[#111]">Historial de citas</h2>
          <button
            onClick={() => navigate('/citas')}
            className="text-[12px] text-[#5b87d8] font-semibold"
            type="button"
          >
            Ver calendario
          </button>
        </div>
        {historialAppointments.length > 0 ? (
          <div className="space-y-2">
            {historialAppointments.map((appt, idx) => {
              const idCita = appt.id_cita || appt.idCita
              const isCompleted = String(appt.estado || '').toUpperCase().includes('COMPLET')
              return (
                <button
                  key={idCita || idx}
                  onClick={() => idCita && navigate(`/citas/${idCita}`)}
                  type="button"
                  className={`w-full rounded-[14px] px-3 py-3 text-left relative overflow-hidden ${
                    isCompleted ? 'bg-[#7a9a52] text-white' : 'bg-[#5b87d8] text-white'
                  }`}
                >
                  <p className="text-[13px] font-semibold">{appt.tipo_cita || appt.tipoCita || 'Consulta'}</p>
                  <p className="text-[11px] opacity-90">{normalizeDate(appt.fecha || appt.fecha_cita || appt.fechaCita)}</p>
                  <span className="mt-1 inline-block text-[9px] font-bold bg-white/25 rounded-[8px] px-2 py-0.5">
                    {appt.estado || 'SIN ESTADO'}
                  </span>
                </button>
              )
            })}
          </div>
        ) : (
          <button
            onClick={() => navigate('/citas')}
            type="button"
            className="w-full rounded-2xl border border-dashed border-pastel-pink bg-pastel-cream/40 py-3 text-sm text-pastel-ink"
          >
            No hay historial de citas disponible
          </button>
        )}

        <div className="mt-6 space-y-2">
          <h3 className="text-[14px] font-bold text-[#111]">Altura (cm)</h3>
          {alturaRegistrosApi.length > 0 ? (
            <ul className="text-[12px] text-slate-600 space-y-1.5">
              {alturaRegistrosApi.slice(0, 20).map((row, idx) => (
                <li key={row.id_registro_altura || idx} className="flex justify-between gap-2 border-b border-[#f0f0f0] pb-1">
                  <span>{normalizeDate(row.fecha_registro || row.fechaRegistro)}</span>
                  <span className="font-medium text-[#111]">{row.altura_cm ?? '—'} cm</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[11px] text-slate-500">Sin registros en servidor. Registra altura desde Artemis Manager o la API.</p>
          )}
        </div>

        <div className="mt-6 space-y-2">
          <h3 className="text-[14px] font-bold text-[#111]">Vacunas</h3>
          {vacunasApi.length > 0 ? (
            <ul className="text-[12px] text-slate-600 space-y-1.5">
              {vacunasApi.slice(0, 20).map((row, idx) => (
                <li key={row.id_registro_vacuna || idx} className="border-b border-[#f0f0f0] pb-1">
                  <p className="font-medium text-[#111]">{row.nombre_vacuna}</p>
                  <p className="text-[11px]">{normalizeDate(row.fecha_aplicacion || row.fechaAplicacion)}</p>
                  {row.proxima_fecha || row.proximaFecha ? (
                    <p className="text-[10px] text-slate-500">Próxima: {normalizeDate(row.proxima_fecha || row.proximaFecha)}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[11px] text-slate-500">Sin vacunas registradas en servidor.</p>
          )}
        </div>

        <div className="mt-6 space-y-2">
          <h3 className="text-[14px] font-bold text-[#111]">Desparasitación</h3>
          {desparasApi.length > 0 ? (
            <ul className="text-[12px] text-slate-600 space-y-1.5">
              {desparasApi.slice(0, 20).map((row, idx) => (
                <li key={row.id_registro_desparasitacion || idx} className="border-b border-[#f0f0f0] pb-1">
                  <p className="font-medium text-[#111]">{row.producto}</p>
                  <p className="text-[10px] text-slate-500">{row.tipo}</p>
                  <p className="text-[11px]">{normalizeDate(row.fecha_aplicacion || row.fechaAplicacion)}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[11px] text-slate-500">Sin registros en servidor.</p>
          )}
        </div>

        <div className="mt-6 space-y-2">
          <h3 className="text-[14px] font-bold text-[#111]">Recordatorios</h3>
          {recordatoriosApi.length > 0 ? (
            <ul className="text-[12px] text-slate-600 space-y-1.5">
              {recordatoriosApi.slice(0, 30).map((row, idx) => (
                <li key={row.id_recordatorio || idx} className="border-b border-[#f0f0f0] pb-1">
                  <p className="font-medium text-[#111]">{row.titulo}</p>
                  <p className="text-[10px] text-slate-500">{row.tipo}</p>
                  <p className="text-[11px]">{normalizeDateTime(row.fecha_hora || row.fechaHora)}</p>
                  <p className="text-[10px]">{row.estado}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[11px] text-slate-500">Sin recordatorios en servidor (citas, medicamentos, peso, etc.).</p>
          )}
        </div>

        <div className="mt-6 space-y-2 pb-4">
          <h3 className="text-[14px] font-bold text-[#111]">Adjuntos (notas, documentos, imágenes)</h3>
          {adjuntosApi.length > 0 ? (
            <ul className="text-[12px] text-slate-600 space-y-1.5">
              {adjuntosApi.map((row, idx) => (
                <li key={row.id_adjunto || idx} className="border-b border-[#f0f0f0] pb-1">
                  <p className="font-medium text-[#111]">{row.titulo || row.tipo}</p>
                  <p className="text-[10px] text-slate-500">{row.tipo}</p>
                  {row.tipo === 'NOTA' && row.contenido ? (
                    <p className="text-[11px] text-slate-700 line-clamp-3">{row.contenido}</p>
                  ) : null}
                  {row.url_publica ? (
                    <a
                      href={absoluteStorageUrl(row.url_publica)}
                      className="text-[11px] text-[#5b87d8] font-semibold"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Ver archivo
                    </a>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[11px] text-slate-500">Sin adjuntos en servidor.</p>
          )}
        </div>
      </section>
      )}

      {activeTab === 'Salud' && (
      <section className="px-4 py-3 space-y-2.5">
        {canRegisterWeight ? (
          <button
            type="button"
            onClick={() => {
              setWeightOpen(true)
              setWeightError(null)
              setWeightSuccess(null)
            }}
            className="w-full h-10 rounded-[20px] border border-[#4a8a28]/25 bg-white text-[#4a8a28] text-sm font-semibold inline-flex items-center justify-center gap-1.5"
          >
            <Scale className="w-4 h-4" />
            Registrar pesaje
          </button>
        ) : null}
        <div className="rounded-2xl border border-pastel-pink bg-pastel-cream/50 px-3.5 py-3">
          <h3 className="text-[14px] font-bold text-[#111] mb-2">Peso vs. ideal</h3>
          <div className="text-[12px] text-slate-600 space-y-1">
            <p>
              Peso actual:{' '}
              {pesoMasReciente?.peso ??
                pesoMasReciente?.valor ??
                mascota?.comparacion_peso?.peso_actual ??
                mascota?.peso ??
                'N/D'}{' '}
              kg
              {pesoMasReciente?.fecha || pesoMasReciente?.fecha_registro ? (
                <span className="text-slate-400">
                  {' '}
                  (
                  {normalizeDate(pesoMasReciente.fecha || pesoMasReciente.fecha_registro || pesoMasReciente.fechaRegistro)}
                  )
                </span>
              ) : null}
            </p>
            <p>Rango ideal: {mascota?.peso_ideal?.peso_minimo ?? 'N/D'} - {mascota?.peso_ideal?.peso_maximo ?? 'N/D'} kg</p>
            <p className="font-semibold text-[#4a8a28]">{mascota?.comparacion_peso?.mensaje || 'Sin información de rango'}</p>
          </div>
        </div>

        {pesoRegistrosApi.length > 1 && (
          <div className="rounded-2xl border border-[#e4e9f0] bg-white px-3.5 py-3">
            <h3 className="text-[14px] font-bold text-[#111] mb-2">Historial de peso</h3>
            <ul className="text-[12px] text-slate-600 space-y-1.5">
              {pesoRegistrosApi.slice(0, 5).map((row, idx) => (
                <li key={row.id || row.id_registro_peso || idx} className="flex justify-between gap-2">
                  <span>{normalizeDate(row.fecha || row.fecha_registro || row.fechaRegistro)}</span>
                  <span className="font-medium text-[#111]">
                    {row.peso ?? row.valor ?? '—'} kg
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="rounded-2xl border border-pastel-pink bg-pastel-cream/50 px-3.5 py-3">
          <h3 className="text-[14px] font-bold text-[#111] mb-2">Condiciones</h3>
          {condicionesForDashboard.length > 0 ? (
            <div className="space-y-2">
              {condicionesForDashboard.slice(0, 12).map((c, idx) => {
                const focusId = condicionFocusRef.current
                const isFocused = focusId && c.id && String(c.id) === String(focusId)
                const fichaId = fichaFocusRef.current
                return (
                  <div
                    key={c.id || `cond-${idx}`}
                    id={c.id ? `condicion-${c.id}` : undefined}
                    className={`rounded-xl border px-3 py-2 ${isFocused ? 'border-[#5b87d8] bg-[#eff6ff]' : 'border-[#e8eef4] bg-white'}`}
                  >
                    <p className="text-[12px] font-semibold text-[#111] truncate">{c.nombre}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {c.estado ? `Estado: ${c.estado}` : '—'}
                      {c.severidad ? ` · Severidad: ${c.severidad}` : ''}
                      {c.fecha ? ` · ${normalizeDate(c.fecha)}` : ''}
                    </p>
                    {isFocused ? (
                      <button
                        type="button"
                        onClick={() => {
                          if (fichaId) navigate(`/fichas/${fichaId}`)
                          else navigate(`/fichas`)
                        }}
                        className="mt-2 inline-flex items-center justify-center w-full h-9 rounded-xl bg-[#0f1623] text-[#00c896] text-[12px] font-semibold"
                      >
                        Registrar seguimiento
                      </button>
                    ) : null}
                  </div>
                )
              })}
              {condicionesForDashboard.length > 12 ? (
                <p className="text-[10px] text-slate-400">Mostrando 12 de {condicionesForDashboard.length}</p>
              ) : null}
            </div>
          ) : (
            <p className="text-[12px] text-[#4a8a28] font-semibold">Sin condiciones en seguimiento</p>
          )}
        </div>
      </section>
      )}

      {activeTab === 'Fichas' && (
      <section className="px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-[15px] font-bold text-[#111]">Fichas clínicas</h2>
          <button
            onClick={() => navigate('/fichas')}
            className="text-[12px] text-[#5b87d8] font-semibold"
            type="button"
          >
            Listado global
          </button>
        </div>
        {fichasApi.length > 0 ? (
          <div className="space-y-2">
            {fichasApi.map((ficha, idx) => {
              const fichaId = ficha.id_ficha || ficha.idFicha
              const num = ficha.numero_ficha || ficha.numeroFicha
              const clinicaNombre = ficha.clinica?.nombre || ficha.clinica?.name || ficha.nombre_clinica
              const titulo =
                ficha.diagnostico ||
                ficha.motivo_consulta ||
                ficha.motivoConsulta ||
                (num ? `Ficha ${num}` : 'Ficha clínica')
              return (
                <button
                  key={fichaId || `f-${idx}`}
                  onClick={() => (fichaId ? navigate(`/fichas/${fichaId}`) : navigate('/fichas'))}
                  type="button"
                  className="w-full rounded-[14px] border border-[#e6ebf4] bg-white px-3 py-3 text-left shadow-sm active:scale-[0.99] transition-transform"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-[13px] font-semibold text-[#111] line-clamp-2 flex-1">{titulo}</p>
                    {num ? (
                      <span className="text-[10px] font-bold text-[#7a8fa6] bg-[#f0f4f8] px-2 py-0.5 rounded-md flex-shrink-0">
                        {num}
                      </span>
                    ) : null}
                  </div>
                  {clinicaNombre ? (
                    <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
                      <Building2 className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{clinicaNombre}</span>
                    </p>
                  ) : null}
                  <p className="text-[11px] text-slate-500 mt-1">{normalizeDate(ficha.fecha || ficha.fecha_consulta || ficha.fechaConsulta)}</p>
                  <p className="text-[11px] text-slate-600 mt-1 line-clamp-2">
                    {ficha.observaciones || ficha.diagnostico_definitivo || ficha.diagnosticoDefinitivo || '—'}
                  </p>
                </button>
              )
            })}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-[#dde3ec] bg-[#f8fafc] px-3 py-4 text-center">
            <p className="text-[13px] text-[#1a2535] font-medium">Sin fichas vinculadas aún</p>
            <p className="text-[11px] text-slate-500 mt-1">
              Si tu veterinario ya cargó visitas, pueden aparecer con unos segundos de demora.
            </p>
            <button
              onClick={() => navigate('/fichas')}
              type="button"
              className="mt-3 text-[12px] text-[#5b87d8] font-semibold"
            >
              Ir al listado de fichas
            </button>
          </div>
        )}
      </section>
      )}

      {shareOpen ? (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end">
          <div className="w-full rounded-t-2xl bg-white p-4 space-y-3 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="text-[15px] font-bold text-[#111]">Compartir acceso temporal</h3>
              <button
                type="button"
                onClick={() => setShareOpen(false)}
                className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {shareError ? <p className="text-[12px] text-red-600">{shareError}</p> : null}
            {!shareResult ? (
              <div className="space-y-3">
                <div>
                  <label className="text-[12px] text-slate-600">Alcance</label>
                  <select
                    value={shareForm.alcance}
                    onChange={(e) => setShareForm((p) => ({ ...p, alcance: e.target.value }))}
                    className="mt-1 w-full h-10 rounded-xl border border-[#d9e2ec] px-3 text-[13px]"
                  >
                    <option value="HISTORIAL_COMPLETO">Historial completo</option>
                    <option value="BASICO">Solo datos básicos</option>
                  </select>
                </div>
                <div>
                  <label className="text-[12px] text-slate-600">Duración</label>
                  <select
                    value={String(shareForm.duracion_minutos)}
                    onChange={(e) => setShareForm((p) => ({ ...p, duracion_minutos: Number(e.target.value) }))}
                    className="mt-1 w-full h-10 rounded-xl border border-[#d9e2ec] px-3 text-[13px]"
                  >
                    <option value="30">30 minutos</option>
                    <option value="60">1 hora</option>
                    <option value="180">3 horas</option>
                    <option value="720">12 horas</option>
                    <option value="1440">24 horas</option>
                  </select>
                </div>
                <button
                  type="button"
                  onClick={createTemporaryShare}
                  disabled={sharing}
                  className="w-full h-11 rounded-[20px] bg-[#0f1623] text-[#00c896] text-sm font-semibold"
                >
                  {sharing ? 'Generando...' : 'Generar QR y enlace'}
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(false)}
                  className="w-full h-10 rounded-[18px] border border-[#d9e2ec] bg-white text-[#111] text-sm font-semibold"
                >
                  Cancelar
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="rounded-xl border border-[#e8eef4] p-3 bg-[#f8fafc]">
                  <p className="text-[11px] text-slate-500">Código</p>
                  <p className="text-[20px] font-bold text-[#111]">{shareResult.codigo_humano}</p>
                  <p className="text-[11px] text-slate-500 mt-1 break-all">Enlace: {shareUrl}</p>
                </div>
                <div className="flex justify-center">
                  <img src={shareQrUrl} alt="QR acceso temporal" className="w-44 h-44 rounded-md border border-[#e8eef4]" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <button type="button" onClick={() => copyText(shareResult.codigo_humano)} className="h-10 rounded-xl border border-[#d9e2ec] text-[12px] font-semibold inline-flex items-center justify-center gap-1">
                    <Copy className="w-3.5 h-3.5" /> Código
                  </button>
                  <button type="button" onClick={() => copyText(shareUrl)} className="h-10 rounded-xl border border-[#d9e2ec] text-[12px] font-semibold inline-flex items-center justify-center gap-1">
                    <Link2 className="w-3.5 h-3.5" /> Enlace
                  </button>
                  <button type="button" onClick={nativeShareLink} className="h-10 rounded-xl border border-[#d9e2ec] text-[12px] font-semibold inline-flex items-center justify-center gap-1">
                    <Share2 className="w-3.5 h-3.5" /> Compartir
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShareOpen(false)
                    setShareResult(null)
                    setShareError(null)
                    setShareForm({ alcance: 'HISTORIAL_COMPLETO', duracion_minutos: 60 })
                  }}
                  className="w-full h-11 rounded-[20px] bg-[#0f1623] text-[#00c896] text-sm font-semibold"
                >
                  Finalizar
                </button>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {weightOpen ? (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end">
          <div className="w-full rounded-t-2xl bg-white p-4 space-y-3 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="text-[15px] font-bold text-[#111]">Registrar pesaje</h3>
              <button
                type="button"
                onClick={() => setWeightOpen(false)}
                className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {weightError ? <p className="text-[12px] text-red-600">{weightError}</p> : null}
            {weightSuccess ? <p className="text-[12px] text-green-700">{weightSuccess}</p> : null}
            <div>
              <label className="text-[12px] text-slate-600">Peso (kg)</label>
              <input
                type="number"
                min="0.1"
                step="0.1"
                value={weightForm.peso}
                onChange={(e) => setWeightForm((p) => ({ ...p, peso: e.target.value }))}
                className="mt-1 w-full h-10 rounded-xl border border-[#d9e2ec] px-3 text-[13px]"
                placeholder="Ej: 12.4"
              />
            </div>
            <div>
              <label className="text-[12px] text-slate-600">Fecha</label>
              <input
                type="date"
                value={weightForm.fecha_registro}
                onChange={(e) => setWeightForm((p) => ({ ...p, fecha_registro: e.target.value }))}
                className="mt-1 w-full h-10 rounded-xl border border-[#d9e2ec] px-3 text-[13px]"
              />
            </div>
            <div>
              <label className="text-[12px] text-slate-600">Tipo de registro</label>
              <select
                value={weightForm.tipo_registro}
                onChange={(e) => setWeightForm((p) => ({ ...p, tipo_registro: e.target.value }))}
                className="mt-1 w-full h-10 rounded-xl border border-[#d9e2ec] px-3 text-[13px]"
              >
                <option value="CASA">Casa</option>
                <option value="VETERINARIA">Veterinaria</option>
              </select>
            </div>
            <div>
              <label className="text-[12px] text-slate-600">Observaciones (opcional)</label>
              <textarea
                rows={3}
                value={weightForm.observaciones}
                onChange={(e) => setWeightForm((p) => ({ ...p, observaciones: e.target.value }))}
                className="mt-1 w-full rounded-xl border border-[#d9e2ec] px-3 py-2 text-[13px]"
                placeholder="Ej: medición luego del paseo"
              />
            </div>
            <button
              type="button"
              onClick={savePesoRegistro}
              disabled={weightSaving}
              className="w-full h-11 rounded-[20px] bg-[#0f1623] text-[#00c896] text-sm font-semibold"
            >
              {weightSaving ? 'Guardando...' : 'Guardar pesaje'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
